import { AddRecordDialog } from "#batteries-included-components/Dialogs/AddRecordDialog";
import {
  useEnhanceRecordResources,
  useListAllRecords,
  useListRecords,
  useRecordMeasurementTypeSpecificTableHeader,
} from "#hooks/adapters/useRecords";
import { FLOW_ATTRIBUTES, useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import BulkRecordStatusChangeButton from "#src/batteries-included-components/Buttons/BulkRecordStatusChangeButton/BulkRecordStatusChangeButton";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import { RecordValueDrawer } from "#src/batteries-included-components/Drawers/RecordValueDrawer";
import { useRecordValueDrawer } from "#src/batteries-included-components/Drawers/RecordValueDrawer/RecordValueDrawer.helper";
import {
  formatStatusFilter,
  getExportGroupBy,
  getGroupBy,
} from "#src/batteries-included-components/Panels/TablePanels/RecordsListTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import InlineRecordValueStatusPicker from "#src/components/Common/InlineRecordValueStatusPicker/InlineRecordValueStatusPicker";
import { RecordValueCell } from "#src/components/Common/RecordValueCell/RecordValueCell";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  useApplyDefaultRecordConfiguration,
  useApplyDefaultRecordConfigurationAsync,
  useApplyDefaultRecordConfigurationByConfigAsync,
} from "#src/queries/recordQueries";
import { linkToAssetDetailPage, linkToRecordDetail } from "#src/utils/links";
import {
  Accordion,
  Button,
  CheckboxInput,
  DataTablePanel,
  Dialog,
  Form,
  HeaderType,
  ItemAction,
  Pill,
  StorageKeys,
  useForm,
} from "@validereinc/common-components";
import type { AssetTypeType, RecordType } from "@validereinc/domain";
import {
  AssetType,
  RecordDomain,
  ResourceDefinitions,
  SortDirection,
} from "@validereinc/domain";
import {
  datetimeFormatter,
  downloadLink,
  fromUTC,
  getDiscreteYearMonths,
  monthFormatter,
  toFlattenedObject,
  toTitleCaseString,
  yearMonthFormatter,
  yearMonthParser,
} from "@validereinc/utilities";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { ASYNC_THRESHOLD } from "./CalculationsTablePanel/CalculationsTablePanel.helper";

export const RecordsListTablePanel = ({
  type,
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  applyAutomationJobStorageKey,
  applySeededAutomationJobStorageKey,
  filterComponentSlot,
}: {
  type: AssetTypeType;
  applyAutomationJobStorageKey: string;
  applySeededAutomationJobStorageKey: string;
  filterComponentSlot: (props: { recordValueTypes: string[] }) => ReactNode;
} & Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
>) => {
  const { getTypeName } = useMeasurementTypes();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const { localize } = useLocalization();
  const [viewFilters] = useStickyState({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const rawFilters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const {
    reporting_group_id,
    ["flow.status"]: flowStatus,
    ["equipment.status"]: equipmentStatus,
    ["facility.status"]: facilityStatus,
    ["equipment.facility.status"]: equipmentFacilityStatus,
    measurement_type: measurement_type_filter,
    ["date_range.from"]: yearMonthFrom,
    ["date_range.to"]: yearMonthTo,
    ...restFilters
  } = toFlattenedObject(rawFilters);
  const [showBulkApplyWarning, setShowBulkApplyWarning] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "year_month",
      sortDirection: SortDirection.DESCENDING,
    },
    rawFilters,
    tableStateStorageKey
  );

  const isPropertyFilterEnabled = !!measurement_type_filter;

  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState({});

  const applyAutomation = useApplyDefaultRecordConfiguration();
  const applyAutomationAsync = useApplyDefaultRecordConfigurationAsync(
    applyAutomationJobStorageKey
  );
  const applySeededAutomationAsync =
    useApplyDefaultRecordConfigurationByConfigAsync(
      applySeededAutomationJobStorageKey
    );
  const assetGroupBy = getGroupBy(type);
  const exportGroupBy = getExportGroupBy(type);
  const dataIngestionResource = {
    ...ResourceDefinitions.record,
    label: {
      singular: `${localize(type)} Record`,
      plural: `${localize(type)} Records`,
    },
  };

  const minYearMonth = yearMonthFrom
    ? yearMonthFormatter(fromUTC(parseISO(yearMonthFrom as string)))
    : yearMonthFormatter(fromUTC(DEFAULT_DATE_RANGES.lastWholeMonth.from));
  const maxYearMonth = yearMonthTo
    ? yearMonthFormatter(fromUTC(parseISO(yearMonthTo as string)))
    : yearMonthFormatter(fromUTC(DEFAULT_DATE_RANGES.lastWholeMonth.to));

  const commonFilters = {
    ...toFlattenedObject(restFilters),
    ...(measurement_type_filter
      ? { measurement_type: { $exact: measurement_type_filter } }
      : {}),
    reporting_group_id: { $exact: reporting_group_id as string },
    ["flow.status"]: formatStatusFilter(flowStatus as string | string[]),
    ...(equipmentStatus
      ? {
          ["equipment.status"]: formatStatusFilter(
            equipmentStatus as string | string[]
          ),
        }
      : {}),
    ...(facilityStatus
      ? {
          ["facility.status"]: formatStatusFilter(
            facilityStatus as string | string[]
          ),
        }
      : {}),
    ...(equipmentFacilityStatus
      ? {
          ["equipment.facility.status"]: formatStatusFilter(
            equipmentFacilityStatus as string | string[]
          ),
        }
      : {}),
    asset_type: { $exact: type },
  };

  const recordParams: Parameters<typeof RecordDomain.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: assetGroupBy,
    meta: {
      includeAssetDetail: true,
    },
    filters: {
      ...(commonFilters as any),
      $and: [
        {
          year_month: {
            $gte: minYearMonth,
          } as any,
        },
        {
          year_month: {
            $lte: maxYearMonth,
          } as any,
        },
      ],
    },
  };

  const isRecordQueryEnabled = !!reporting_group_id;

  const recordQuery = useListRecords(recordParams, {
    enabled: isRecordQueryEnabled,
  });

  const allMeasurementTypesQuery = useListAllRecords(
    {
      ...structuredClone(recordParams),
      meta: {
        includeAssetDetail: false,
      },
      groupBy: ["measurement_type"],
      sortBy: "measurement_type",
      sortDirection: SortDirection.ASCENDING,
      pageSize: 500,
      page: 1,
    },
    {
      enabled: isRecordQueryEnabled,
    }
  );

  const unenhancedItems = recordQuery?.data?.data ?? [];

  // This hook enhances record values with calculator_result configuration to have estimation_method_entity_id:
  const { recordOrRecords: items, isLoading: isEnhancedRecordsLoading } =
    useEnhanceRecordResources(unenhancedItems, {
      enabled: isPropertyFilterEnabled,
    });

  const propertySpecificHeaders = useRecordMeasurementTypeSpecificTableHeader(
    measurement_type_filter as string
  );

  const { isLoading: isLoadingFlowHeaders, headers: flowHeadersFull } =
    useFlowHeaders<RecordType>({
      prefix: "flow",
      keySubstitutions: {
        [FLOW_ATTRIBUTES.EQUIPMENT_NAME.key]: "flow_equipment_name",
        [FLOW_ATTRIBUTES.FACILITY_NAME.key]: "flow_facility_name",
      },
      sortSubstitutions: {
        [FLOW_ATTRIBUTES.EQUIPMENT.key]: "flow_equipment_name",
        [FLOW_ATTRIBUTES.FACILITY.key]: "flow_facility_name",
      },
    });
  const flowHeaders = flowHeadersFull.filter(
    ({ key }) => !["flow.product_category", "flow.product_type"].includes(key)
  );

  const { openDrawer, drawerState, navigationProps, onClose, recordValue } =
    useRecordValueDrawer({
      items: (items as RecordType[]) ?? ([] as RecordType[]),
      itemToAssetIdFunction: (i: any) =>
        i?.["flow.id"] ?? i?.["facility.id"] ?? i?.["equipment.id"],
    });

  const handleExport = async () => {
    setIsExporting(true);

    const exportRecordParams: Parameters<typeof RecordDomain.exportList>[0] = {
      page: tableState.page,
      pageSize: tableState.pageSize,
      groupBy: exportGroupBy,
      filters: recordParams.filters,
    };

    try {
      const recordReport = await RecordDomain.exportList(exportRecordParams);

      downloadLink(
        recordReport.s3_download_link,
        `Records - ${datetimeFormatter(new Date())}`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  const getAssetHeaders = (
    assetType: AssetTypeType
  ): Array<HeaderType<RecordType>> => {
    switch (assetType) {
      case AssetType.FACILITY:
        return [
          {
            key: "facility.name",
            label: localize("Facility"),
            isSortable: true,
            renderComponent: ({ item }: any) => (
              <RoutingLink
                to={linkToAssetDetailPage(
                  AssetType.FACILITY,
                  item?.["facility.id"]
                )}
              >
                {item?.["facility.name"]}
              </RoutingLink>
            ),
          },
        ];
      case AssetType.EQUIPMENT:
        return [
          {
            key: "equipment.name",
            label: localize("Equipment"),
            isSortable: true,
            renderComponent: ({ item }) => (
              <RoutingLink
                to={linkToAssetDetailPage(
                  AssetType.EQUIPMENT,
                  (item as any)?.["equipment.id"]
                )}
              >
                {(item as any)?.["equipment.name"]}
              </RoutingLink>
            ),
          },
          {
            key: "equipment.status",
            label: `${localize("Equipment")} Status`,
            isSortable: true,
            renderComponent: ({ item }) => (
              <Pill
                variant={
                  (item as any)?.["equipment.status"] === "active"
                    ? "success"
                    : "default"
                }
              >
                {toTitleCaseString((item as any)?.["equipment.status"], {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            ),
          },
          {
            key: "equipment.type.name",
            label: localize("Equipment Type"),
            isSortable: true,
            renderComponent: ({ item }) =>
              (item as any)?.["equipment.type.name"] ?? "-",
          },
          {
            key: "equipment_facility_name",
            label: localize("Facility"),
            isSortable: true,
            renderComponent: ({ item }) => (
              <RoutingLink
                to={linkToAssetDetailPage(
                  AssetType.FACILITY,
                  (item as any)?.equipment?.facility_id
                )}
              >
                {(item as any)?.equipment_facility_name}
              </RoutingLink>
            ),
          },
        ];
      case AssetType.FLOW:
      default:
        return flowHeaders;
    }
  };

  const selectedRecordIds = Object.keys(selectedRecords);
  const applyRecordAutomation = () => {
    const count = selectedRecordIds.length;
    if (!count || count > ASYNC_THRESHOLD) {
      setShowBulkApplyWarning(true);
    } else {
      applyAutomation.mutate({
        filters: { id: selectedRecordIds },
        overwriteAllValues: false,
      });
    }
  };

  const form = useForm({});
  useEffect(() => {
    form.reset();
  }, [showBulkApplyWarning]);

  const applyBulkRecordAutomation = form.handleSubmit((values) => {
    const { overwrite_all_values } = values;
    setShowBulkApplyWarning(false);
    if (selectedRecordIds.length) {
      const applyAutomationFilters = {
        ...commonFilters,
        $and: [
          {
            year_month: {
              $gte: minYearMonth,
            },
          },
          {
            year_month: {
              $lte: maxYearMonth,
            },
          },
        ],
      };
      applyAutomationAsync.mutate({
        filters: applyAutomationFilters as any,
        overwriteAllValues: overwrite_all_values,
      });
    } else {
      const applySeededAutomationFilters = {
        ...commonFilters,
        year_month: getDiscreteYearMonths(minYearMonth, maxYearMonth),
      };
      applySeededAutomationAsync.mutate({
        filters: applySeededAutomationFilters as any,
        overwriteAllValues: overwrite_all_values,
      });
    }
  });

  const unfilteredRecordValueTypes = Array.from(
    new Set(
      (allMeasurementTypesQuery.data?.data ?? [])?.flatMap(
        ({ values }: RecordType) =>
          values.map(
            ({ measurement_type }: { measurement_type: string }) =>
              measurement_type
          )
      )
    )
  );

  const recordValueTypes = isPropertyFilterEnabled
    ? [measurement_type_filter as string]
    : unfilteredRecordValueTypes;

  const isLoading =
    isLoadingFlowHeaders ||
    (isRecordQueryEnabled && recordQuery.isLoading) ||
    (isRecordQueryEnabled && allMeasurementTypesQuery.isLoading) ||
    (isPropertyFilterEnabled && isEnhancedRecordsLoading);

  const headers: Array<HeaderType<RecordType>> = [
    {
      key: "year_month",
      label: "Time Period",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={linkToRecordDetail(
            type,
            item?.["flow.id"] ??
              (item as any)?.["facility.id"] ??
              (item as any)?.["equipment.id"],
            item.id
          )}
        >
          {monthFormatter(yearMonthParser(item?.year_month))}
        </RoutingLink>
      ),
    },
    ...getAssetHeaders(type),
    {
      key: "status",
      label: "Record Status",
      isSortable: false,
      renderComponent: ({ item }) => {
        const { values, id } = item;
        return (
          <InlineRecordValueStatusPicker
            recordId={id}
            values={values}
          />
        );
      },
    },
    ...recordValueTypes.map((key) => ({
      key,
      label: getTypeName(key),
      alignment: "right" as any,
      renderComponent: ({ item }: any) => {
        const { values, year_month, id } = item;
        const findValue = values?.find(
          ({ measurement_type }: { measurement_type: string }) =>
            measurement_type === key
        );

        return (
          <RecordValueCell
            value={findValue}
            onClick={() => {
              openDrawer(
                item?.["flow.id"] ??
                  item?.["facility.id"] ??
                  item?.["equipment.id"],
                id,
                year_month,
                key
              );
            }}
          />
        );
      },
    })),
    ...propertySpecificHeaders,
  ];

  const actionRow = [
    <BulkRecordStatusChangeButton
      key="status-picker"
      filter={
        Object.keys(selectedRecords).length > 0
          ? { id: Object.keys(selectedRecords) }
          : (recordParams.filters ?? {})
      }
      recordsCount={
        Object.keys(selectedRecords).length > 0
          ? Object.keys(selectedRecords).length
          : (recordQuery.data?.total_entries ?? 0)
      }
    />,
    <Button
      icon="play-simple"
      key="apply-automation"
      isLoading={applyAutomation.isLoading || applyAutomationAsync.isLoading}
      onClick={applyRecordAutomation}
    >
      Apply Automation
    </Button>,
    <PermissionAwareExportButton
      key="export-flow-records"
      onClick={handleExport}
      isExporting={isExporting}
    />,
    ...(isDataIngestionEnabled
      ? [
          <ImportDataAction
            key={`import-${type}-records`}
            resource={dataIngestionResource}
          />,
        ]
      : []),
  ];

  const getItemActions = ({ item }: { item: RecordType }): ItemAction[] => {
    return [
      {
        label: "Apply Automation",
        buttonProps: {
          onClick: () =>
            applyAutomation.mutate({
              filters: { id: item.id },
              overwriteAllValues: false,
            }),
        },
      },
    ];
  };

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{ title: "Records", actionRow }}
        dataTableProps={{
          headers,
          items: (items as unknown as RecordType[]) ?? ([] as RecordType[]),
          isLoading,
          selected: selectedRecords,
          getItemId: (item) => item.id,
          onSelectionChange: setSelectedRecords,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: recordQuery.data?.total_entries,
          },
        }}
        filterComponent={filterComponentSlot({
          recordValueTypes: unfilteredRecordValueTypes,
        })}
      />
      <AddRecordDialog
        isOpen={isAddDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={() => {
          recordQuery.refetch();
        }}
        assetType={type}
        reportingGroupId={reporting_group_id as string}
      />
      <Dialog
        key="large-apply-dialog"
        title="Apply Record Automation?"
        isOpen={showBulkApplyWarning}
        onClose={() => setShowBulkApplyWarning(false)}
        actionRow={[
          <Button
            key="apply-automation-action"
            variant="primary"
            onClick={applyBulkRecordAutomation}
          >
            Apply Automation
          </Button>,
        ]}
      >
        <p>
          {selectedRecordIds.length
            ? `You have selected to apply record automation to ${selectedRecordIds.length} selected records.`
            : `You have selected to apply record automation based on filtered configurations.`}
        </p>
        <Accordion>
          <Accordion.AccordionPanel
            title="Advanced Configuration"
            dataKey="advanced-configuration"
          >
            <p style={{ width: 484 }}>
              Applying record automation will populate record values based off
              predefined automations to all applicable empty record value
              fields. Manually modified values will not be overwritten unless
              otherwise specified.
            </p>
            <Form {...form}>
              <CheckboxInput
                name="overwrite_all_values"
                label="Overwrite manual values with automation?"
                isLabelShown={false}
                isFluid
              />
            </Form>
          </Accordion.AccordionPanel>
        </Accordion>
      </Dialog>

      <RecordValueDrawer
        recordValue={recordValue}
        state={drawerState}
        onClose={onClose}
        assetType={type}
        applyAutomation={(id) =>
          applyAutomation.mutate({ filters: { id }, overwriteAllValues: false })
        }
        navgivationProps={navigationProps}
      />
    </>
  );
};
